import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Switch } from "@mui/material";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import { FilterValue } from "@components/filters";
import {
  MobileListSettingsButton,
  MultiSelect,
  SearchSetting,
  SettingsFieldGroup,
  SettingsSections,
} from "@components/MobileList";
import { useMandatorVisibility } from "@helpers";

interface Props {
  groups: AccountTypeEnum[];
  selectedChips: FilterValue[];
  setSelectedChips: Dispatch<SetStateAction<FilterValue[]>>;
  hiddenAccountsCount: number;
  showHidden: boolean;
  setShowHidden: Dispatch<SetStateAction<boolean>>;
  filter: string;
  setFilter: (value: string) => void;
}

export const AccountsListSettings = ({
  groups,
  selectedChips,
  setSelectedChips,
  hiddenAccountsCount,
  showHidden,
  setShowHidden,
  filter,
  setFilter,
}: Props) => {
  const { t } = useTranslation();
  const showsFilters = useMandatorVisibility({ hideFor: ["kpmg-germany"] });
  const selectedGroups = groups.filter((group) =>
    selectedChips.includes(group)
  );
  const hasChanges = filter !== "" || selectedGroups.length > 0 || showHidden;

  return (
    <MobileListSettingsButton
      hasChanges={hasChanges}
      canReset={hasChanges}
      onReset={() => {
        setFilter("");
        setSelectedChips(["all"]);
        setShowHidden(false);
      }}
    >
      <SettingsSections testId="accounts-list-settings">
        <SettingsFieldGroup>
          <SearchSetting
            label={t("depotsAccountsPage.mobile.search")}
            value={filter}
            onChange={setFilter}
          />
          {showsFilters && groups.length > 1 && (
            <MultiSelect
              label={t("depotsAccountsPage.mobile.category")}
              options={groups.map((group) => ({
                value: group,
                label: t(`depotsAccountsPage.chips.${group}`),
              }))}
              selected={selectedGroups}
              onChange={(next) =>
                setSelectedChips(next.length ? next : ["all"])
              }
              allLabel={t("depotsAccountsPage.mobile.allCategories")}
              countLabel={(count) =>
                t("depotsAccountsPage.mobile.categoriesSelected", { count })
              }
            />
          )}
          {showsFilters && hiddenAccountsCount > 0 && (
            <FormControlLabel
              control={
                <Switch
                  size="medium"
                  checked={showHidden}
                  onChange={(_, checked) => setShowHidden(checked)}
                  data-testid="hidden-switch"
                />
              }
              label={t("depotsAccountsPage.mobile.showHidden", {
                count: hiddenAccountsCount,
              })}
              sx={{ m: 0 }}
            />
          )}
        </SettingsFieldGroup>
      </SettingsSections>
    </MobileListSettingsButton>
  );
};
